/* eslint-disable jsx-a11y/click-events-have-key-events */

import { classNames } from "helpers/commonHelpers";
import DualListItem from "./components/DualListItem";
import { DualListOption, DualListProps } from "./DualList.types";
import "./DualList.scss";

/**
 * DualList - A controlled component for dual-list selection.
 *
 * This is a controlled component that should be used when you need to manage
 * the selection state externally. It accepts `value` and `onChange` props.
 *
 * For React Hook Form integration, use the `DualListField` component instead,
 * which wraps this component with RHF's Controller.
 *
 * @example
 * // Standalone usage (controlled)
 * const [selected, setSelected] = useState<string[]>([]);
 * <DualList options={options} value={selected} onChange={setSelected} />
 *
 * @example
 * // React Hook Form usage
 * <DualListField name="selectedItems" options={options} />
 */
function DualList(props: DualListProps) {
  const { options, value, onChange, label, optionsListLabel, selectedListLabel, error } = props;
  const selectedValues = Array.isArray(value) ? value : [];

  const unselectedOptions = options?.filter((item) => !selectedValues.includes(item.value)) || [];
  const selectedOptions = options?.filter((item) => selectedValues.includes(item.value)) || [];

  const handleSelect = (selectedValue: unknown) => {
    if (selectedValues.includes(selectedValue)) {
      return;
    }
    onChange([...selectedValues, selectedValue]);
  };

  const handleSelectAll = (filteredOptions: DualListOption[]) => {
    onChange([...selectedValues, ...filteredOptions.map((item) => item.value)]);
  };

  const handleDeselect = (selectedValue: unknown) => {
    if (!selectedValues.includes(selectedValue)) {
      return;
    }
    onChange(selectedValues.filter((item) => item !== selectedValue));
  };

  const handleDeselectAll = (filteredOptions: DualListOption[]) => {
    const filteredOptionsHashMap = filteredOptions.reduce<Record<string | number, boolean>>((acc, item: any) => {
      acc[item.value] = true;
      return acc;
    }, {});

    onChange(selectedValues.filter((item: any) => !filteredOptionsHashMap[item]));
  };

  return (
    <div className={classNames("dual-list", { "dual-list--error": error })}>
      {label && <div className="text-primary text-sm font-medium mb-md">{label}</div>}
      <div className="dual-list__lists">
        <div className="dual-list__main-container">
          <DualListItem
            actionButtonLabel="Select all"
            actionButtonFilterLabel="Select filtered"
            actionButtonVariant="link-color"
            label={optionsListLabel}
            options={unselectedOptions}
            onChange={handleSelect}
            actionButtonOnClick={handleSelectAll}
            showNoDataMessage={!options?.length}
          />
        </div>
        <div className="dual-list__main-container">
          <DualListItem
            actionButtonLabel="Remove all"
            actionButtonFilterLabel="Remove filtered"
            actionButtonVariant="link-error"
            label={selectedListLabel}
            options={selectedOptions}
            onChange={handleDeselect}
            actionButtonOnClick={handleDeselectAll}
            showNoDataMessage={!options?.length}
          />
        </div>
      </div>
    </div>
  );
}

export default DualList;

DualList.defaultProps = {
  label: undefined,
  optionsListLabel: undefined,
  selectedListLabel: undefined,
};
